/**
 * @file widget-languages.ts
 * @desc ISO 639-1 language code to display name mapping for the WFW web widget.
 *
 * Supports the top 7 global languages chosen for Phase 1 based on market reach
 * and common customer segments. Codes align with ElevenLabs ConvAI language
 * identifiers so they can be passed directly as dynamic variables.
 *
 * Usage:
 *   import { WIDGET_LANGUAGES, LANGUAGE_DISPLAY_NAMES } from '@/lib/i18n/widget-languages'
 *   // Iterate WIDGET_LANGUAGES for the ordered list.
 *   // Look up LANGUAGE_DISPLAY_NAMES[code] for the UI label.
 */

/**
 * Ordered list of supported language codes (ISO 639-1).
 * The first entry is the default when no language is selected.
 * Maximum 7 languages are shown in the widget dropdown to keep the UI compact.
 */
export const WIDGET_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt', 'zh', 'ar'] as const

/** Type representing any of the 7 supported language codes. */
export type WidgetLanguageCode = (typeof WIDGET_LANGUAGES)[number]

/**
 * Maps each ISO 639-1 code to the display name shown in the language selector.
 * Display names use the native script so users can recognise their language
 * regardless of the widget's current language setting.
 */
export const LANGUAGE_DISPLAY_NAMES: Record<WidgetLanguageCode, string> = {
  en: 'English',
  es: 'Espanol',
  fr: 'Francais',
  de: 'Deutsch',
  pt: 'Portugues',
  zh: 'Zhongwen',
  ar: 'Arabic',
}

/**
 * Returns true if the given string is a valid supported language code.
 * Used by the embed agent to validate the language read from the config response
 * before passing it to the widget and API calls.
 */
export function isValidLanguageCode(code: string): code is WidgetLanguageCode {
  return (WIDGET_LANGUAGES as readonly string[]).includes(code)
}
